import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import EmailIcon from "@mui/icons-material/Email";
import { useTranslation } from "react-i18next";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useEffect, useState } from "react";
import {
  CONTRACTS_ENDPOINT,
  CONTRACTS_AUTOMATION_BULK_UPDATE_ENDPOINT,
  SETTLEMENTS_NEEDS_REMINDER_ENDPOINT,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
} from "../../consts";
import ContractMultistepForm from "../../components/ui/contractMultistepForm/contractMultistepForm.component";
import { useParams, useNavigate } from "react-router-dom";
import { useCRUD } from "../../api/crud";
import axios, { useApiRequest } from "../../api/axios";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { useHasPermission } from "../../contexts/auth";
import { SendReminder } from "../../components/settlements/sendReminder/sendReminder.component";
import ContractEndDatePicker from "../../components/ui/contractEndDatePicker/contractEndDatePicker.component";
import { Dayjs } from "dayjs";
import { Contract, ContractAutomationLevelValue } from "../../types/contracts";
import { CounterPriorityProviderValue } from "../../types/tenantSettings";

// The backend represents "no automation"/"inherit tenant default" as null; the
// Select can't take a null value directly, so the empty string stands in for
// it in the UI only.
const MANUAL_VALUE = "";
const INHERIT_VALUE = "";

const AUTOMATION_LEVEL_OPTIONS: { value: ContractAutomationLevelValue | typeof MANUAL_VALUE; label: string }[] = [
  { value: MANUAL_VALUE, label: "Manual" },
  { value: "C", label: "Auto-confirm settlements" },
  { value: "I", label: "Auto-confirm and invoice" },
  { value: "K", label: "Auto-confirm, invoice and send to KSeF" },
  { value: "F", label: "Auto-confirm, invoice, send to KSeF and send to client" },
];

const COUNTER_PRIORITY_PROVIDER_OPTIONS: { value: CounterPriorityProviderValue | typeof INHERIT_VALUE; label: string }[] = [
  { value: INHERIT_VALUE, label: "Inherit tenant default" },
  { value: "U", label: "User" },
  { value: "E", label: "Email report" },
  { value: "P", label: "PDF" },
];

const toUiValue = (level: ContractAutomationLevelValue | null | undefined): string => level ?? MANUAL_VALUE;
const fromUiValue = (value: string): ContractAutomationLevelValue | null =>
  value === MANUAL_VALUE ? null : (value as ContractAutomationLevelValue);

const toCounterPriorityUiValue = (value: CounterPriorityProviderValue | null | undefined): string =>
  value ?? INHERIT_VALUE;
const fromCounterPriorityUiValue = (value: string): CounterPriorityProviderValue | null =>
  value === INHERIT_VALUE ? null : (value as CounterPriorityProviderValue);

export const ContractsDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove, get } = useCRUD(CONTRACTS_ENDPOINT);
  const {
    loading: revokeEndLoading,
    error: revokeEndError,
    executeRequest: executeRevokeEndRequest,
  } = useApiRequest();
  const [updateContractOpened, setUpdateContractOpened] = useState<boolean>(false);
  const [deleteContractOpened, setDeleteContractOpened] = useState<boolean>(false);
  const [revokeEndOpened, setRevokeEndOpened] = useState<boolean>(false);
  // undefined = no selection made yet (submit disabled); null = "Indefinitely" chosen
  const [newEndDate, setNewEndDate] = useState<Dayjs | null | undefined>(undefined);
  const [newIndefiniteAfterEndDate, setNewIndefiniteAfterEndDate] = useState(false);
  const [contract, setContract] = useState<Contract | null>(null);
  const {
    loading: automationLevelLoading,
    error: automationLevelError,
    executeRequest: executeAutomationLevelRequest,
  } = useApiRequest();
  const [automationLevelOpened, setAutomationLevelOpened] = useState<boolean>(false);
  const [newAutomationLevel, setNewAutomationLevel] = useState<ContractAutomationLevelValue | null>(null);
  const [newCounterPriorityProvider, setNewCounterPriorityProvider] = useState<CounterPriorityProviderValue | null>(null);
  const [sendReminderOpened, setSendReminderOpened] = useState<boolean>(false);
  const [settlementsNeedingReminder, setSettlementsNeedingReminder] = useState<number>(0);
  const canChangeContract = useHasPermission("settlement.change_contract");
  const canDeleteContract = useHasPermission("settlement.delete_contract");
  const canChangeSettlement = useHasPermission("settlement.change_settlement");

  useEffect(() => {
    const fetchContract = async () => {
      if (!id) return;
      const response = await get(id);
      if (response?.data) {
        setContract(response.data);
      }
    };

    fetchContract();
    window.addEventListener("contractUpdated", fetchContract);
    return () => {
      window.removeEventListener("contractUpdated", fetchContract);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id || !canChangeSettlement) {
      setSettlementsNeedingReminder(0);
      return;
    }

    let mounted = true;

    // Only the count matters here - the dialog fetches the actual rows - so
    // a one-row page is enough to know whether the action has anything to do.
    const fetchSettlementsNeedingReminder = async () => {
      try {
        const response = await axios.get(SETTLEMENTS_NEEDS_REMINDER_ENDPOINT, {
          params: { contract_id: id, page: 1, page_size: 1 },
        });
        if (mounted) {
          setSettlementsNeedingReminder(Number(response.data?.count) || 0);
        }
      } catch {
        if (mounted) {
          setSettlementsNeedingReminder(0);
        }
      }
    };

    fetchSettlementsNeedingReminder();
    window.addEventListener("settlementUpdated", fetchSettlementsNeedingReminder);
    window.addEventListener("contractUpdated", fetchSettlementsNeedingReminder);
    return () => {
      mounted = false;
      window.removeEventListener("settlementUpdated", fetchSettlementsNeedingReminder);
      window.removeEventListener("contractUpdated", fetchSettlementsNeedingReminder);
    };
  }, [id, canChangeSettlement]);

  const handleDeleteContract = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t("Contract deleted successfully"), SUCCESS_SNACKBAR);
      setDeleteContractOpened(false);
      navigate("/contracts");
    } else {
      enqueueSnackbar(t("Failed to delete contract"), ERROR_SNACKBAR);
    }
  };

  const handleRevokeContractEnd = async () => {
    if (revokeEndLoading || !id || newEndDate === undefined) {
      return;
    }

    const result = await executeRevokeEndRequest(() =>
      axios.post(`${CONTRACTS_ENDPOINT}${id}/revoke-end/`, {
        end_date: newEndDate ? newEndDate.format("YYYY-MM-DD") : null,
        // The backend coerces this to false when there's no end_date, so it's
        // safe to send as-is - the date and the flag are independent choices.
        indefinite_after_end_date: newIndefiniteAfterEndDate,
      })
    );

    if (result?.success) {
      enqueueSnackbar(t("Contract end revoked successfully"), SUCCESS_SNACKBAR);
      setRevokeEndOpened(false);
      setNewEndDate(undefined);
      setNewIndefiniteAfterEndDate(false);
      window.dispatchEvent(new Event("contractUpdated"));
    } else {
      enqueueSnackbar(t("Failed to revoke contract end"), ERROR_SNACKBAR);
    }
  };

  const handleSetAutomationLevel = async () => {
    if (automationLevelLoading || !id) {
      return;
    }

    const result = await executeAutomationLevelRequest(() =>
      axios.patch(CONTRACTS_AUTOMATION_BULK_UPDATE_ENDPOINT, [
        {
          id: Number(id),
          automation_level: newAutomationLevel,
          counter_priority_provider: newCounterPriorityProvider,
        },
      ])
    );

    if (result?.success) {
      enqueueSnackbar(t("Automation level updated successfully"), SUCCESS_SNACKBAR);
      setAutomationLevelOpened(false);
      window.dispatchEvent(new Event("contractUpdated"));
    } else {
      enqueueSnackbar(t("Failed to update automation level"), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t("Update contract"),
      onClick: () => {
        setUpdateContractOpened(true);
      },
      disabled: !canChangeContract,
    },
    ...(contract?.ended
      ? [
          {
            icon: <RestoreIcon />,
            label: t("Revoke end of contract"),
            onClick: () => {
              setNewEndDate(undefined);
              setNewIndefiniteAfterEndDate(false);
              setRevokeEndOpened(true);
            },
            disabled: !canChangeContract,
          },
        ]
      : []),
    {
      icon: <AutoModeIcon />,
      label: t("Set automation level"),
      onClick: () => {
        setNewAutomationLevel(contract?.automation_level ?? null);
        setNewCounterPriorityProvider(contract?.counter_priority_provider ?? null);
        setAutomationLevelOpened(true);
      },
      disabled: !canChangeContract,
    },
    {
      icon: <EmailIcon />,
      label: t("Send reminder"),
      onClick: () => {
        setSendReminderOpened(true);
      },
      disabled: !canChangeSettlement || settlementsNeedingReminder === 0,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete contract"),
      onClick: () => {
        setDeleteContractOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteContract,
    },
  ];

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.message ?? error?.detail ?? "";

  const revokeEndErrorMessage =
    typeof revokeEndError === "string"
      ? revokeEndError
      : revokeEndError?.message ??
        revokeEndError?.detail ??
        (revokeEndError?.end_date ? String(revokeEndError.end_date) : "");

  const automationLevelErrorMessage =
    typeof automationLevelError === "string"
      ? automationLevelError
      : automationLevelError?.message ?? automationLevelError?.detail ?? "";

  const deleteModalContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t(
          "Deleting this contract will permanently remove it from the system. This action cannot be undone."
        )}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t(
            "This action cannot be undone. All associated data will be permanently deleted."
          )}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteContract}
        disabled={loading}
      >
        {loading ? t("Processing...") : t("Confirm deletion")}
      </Button>
    </Box>
  );

  const revokeEndModalContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t(
          "Are you sure you want to make this contract active again? Please set a new end date for the contract."
        )}
      </Typography>

      <ContractEndDatePicker
        label={t("New end date")}
        value={newEndDate}
        onChange={setNewEndDate}
        indefiniteAfterEndDate={newIndefiniteAfterEndDate}
        onIndefiniteAfterEndDateChange={setNewIndefiniteAfterEndDate}
      />

      {revokeEndErrorMessage && <Alert severity="error">{revokeEndErrorMessage}</Alert>}

      <Button
        variant="contained"
        onClick={handleRevokeContractEnd}
        disabled={revokeEndLoading || newEndDate === undefined}
      >
        {revokeEndLoading ? t("Processing...") : t("Confirm")}
      </Button>
    </Box>
  );

  const automationLevelModalContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t("Select the automation level for this contract.")}
      </Typography>

      <FormControl size="small" fullWidth>
        <Select
          value={toUiValue(newAutomationLevel)}
          displayEmpty
          onChange={(event: SelectChangeEvent) =>
            setNewAutomationLevel(fromUiValue(event.target.value))
          }
        >
          {AUTOMATION_LEVEL_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary">
        {t("Select the equipment counter priority provider for this contract.")}
      </Typography>

      <FormControl size="small" fullWidth>
        <Select
          value={toCounterPriorityUiValue(newCounterPriorityProvider)}
          displayEmpty
          onChange={(event: SelectChangeEvent) =>
            setNewCounterPriorityProvider(fromCounterPriorityUiValue(event.target.value))
          }
        >
          {COUNTER_PRIORITY_PROVIDER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {automationLevelErrorMessage && <Alert severity="error">{automationLevelErrorMessage}</Alert>}

      <Button
        variant="contained"
        onClick={handleSetAutomationLevel}
        disabled={automationLevelLoading}
      >
        {automationLevelLoading ? t("Processing...") : t("Confirm")}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateContractOpened}
        setOpened={setUpdateContractOpened}
        title={t("Update contract")}
        content={
          <ContractMultistepForm
            id={id}
            onSuccess={() => {
              setUpdateContractOpened(false);
              window.dispatchEvent(new Event("contractUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deleteContractOpened}
        setOpened={setDeleteContractOpened}
        title={t("Delete contract")}
        content={deleteModalContent}
      />
      <CustomModal
        opened={revokeEndOpened}
        setOpened={setRevokeEndOpened}
        title={t("Revoke end of contract")}
        content={revokeEndModalContent}
      />
      <CustomModal
        opened={automationLevelOpened}
        setOpened={setAutomationLevelOpened}
        title={t("Set automation level")}
        content={automationLevelModalContent}
      />
      <CustomModal
        opened={sendReminderOpened}
        setOpened={setSendReminderOpened}
        title={t("Send reminder")}
        content={
          <SendReminder
            preselectedContractId={id}
            onSuccess={() => {
              setSendReminderOpened(false);
              window.dispatchEvent(new Event("settlementUpdated"));
            }}
          />
        }
      />
    </>
  );
};
